/**
 * Which search you were looking at when you left a list.
 *
 * Every detail page in this tree opens with "← All signups", and that link goes
 * to the *unfiltered* list. So the ordinary loop — narrow to a facility and a
 * week, open a row, read it, go back, open the next row — loses the search on
 * every single trip, and the filters get retyped once per row. The browser's own
 * Back button does the right thing, but the link on the page does not, and the
 * link is what is in front of you.
 *
 * `sessionStorage`, not `localStorage`: a search you were in the middle of is a
 * property of this sitting, and a filter silently surviving until tomorrow
 * morning is the same "why is this list empty" problem in a slower form.
 */

const KEY = 'ws-next-last-list';

const read = () => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(KEY) || '{}');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch (error) {
    return {};
  }
};

export const rememberListSearch = (pathname, search) => {
  if (!pathname) return;
  try {
    const all = read();

    // An empty search is worth storing: it is how "I cleared the filters" gets
    // remembered rather than resurrecting the previous ones.
    all[pathname] = search || '';
    sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch (error) {
    // Storage off or full costs the shortcut, not the page.
  }
};

/**
 * The list path with whatever search was last on it. Falls back to the bare
 * path, so a link built from this is never worse than the one it replaced.
 */
export const listHref = pathname => {
  const remembered = read()[pathname];
  return typeof remembered === 'string' && remembered ? `${pathname}${remembered}` : pathname;
};

/** Whether going back will restore a search, so the link can say so. */
export const hasRememberedSearch = pathname => {
  const remembered = read()[pathname];
  return typeof remembered === 'string' && remembered.length > 1;
};
